import { Account } from '../schema/accountSchema'
import { generateAccountPeople } from '../utils/enhancedPeopleGenerator'
/**
 * Generated Accounts Dataset
 * 100 professional, realistic demo accounts across diverse industries
 */
interface OrderData {
  id: string
  title: string
  status: 'In Progress' | 'Delivered' | 'On Hold' | 'Planning'
  value: number
  date: string
}
function generateOrders(accountId: string): OrderData[] {
  const orderTitles = [
    'Q1 Direct Mail Campaign',
    'Brand Refresh Package',
    'Holiday Catalog Production',
    'Digital Asset Library',
    'Store Signage Rollout',
    'Product Launch Materials',
    'Annual Report Design',
    'Seasonal Promotion Kit',
    'Corporate Brochure Update',
    'Trade Show Materials',
  ]
  const statuses: Array<'In Progress' | 'Delivered' | 'On Hold' | 'Planning'> =
    ['In Progress', 'Delivered', 'On Hold', 'Planning']
  const orders: OrderData[] = []
  const numOrders = 3 + Math.floor(Math.random() * 3) // 3-5 orders
  for (let i = 0; i < numOrders; i++) {
    const daysAgo = Math.floor(Math.random() * 60)
    const date = new Date()
    date.setDate(date.getDate() - daysAgo)
    orders.push({
      id: `${accountId}-order-${i + 1}`,
      title: orderTitles[Math.floor(Math.random() * orderTitles.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      value: 25000 + Math.floor(Math.random() * 1175000), // $25K - $1.2M
      date: date.toISOString(),
    })
  }
  return orders
}
function generateAccount(
  index: number,
  name: string,
  industry: string,
  products: string[],
): Account {
  const accountId = `acct-gen-${index.toString().padStart(3, '0')}`
  const { client_contacts, quad_team } = generateAccountPeople(accountId, name)
  const statuses: Array<'Green' | 'Yellow' | 'Red'> = ['Green', 'Yellow', 'Red']
  const health = statuses[Math.floor(Math.random() * statuses.length)]
  const marginHealthOptions = ['Healthy', 'Moderate', 'At Risk']
  const marginHealth =
    marginHealthOptions[Math.floor(Math.random() * marginHealthOptions.length)]
  const daysAgo = Math.floor(Math.random() * 30)
  const lastSync = new Date()
  lastSync.setDate(lastSync.getDate() - daysAgo)
  const orders = generateOrders(accountId)
  return {
    id: accountId,
    name,
    logo_url: `https://placehold.co/96x48?text=${encodeURIComponent(name.split(' ')[0])}`,
    industry,
    region: 'North America',
    health,
    primary_owner: quad_team[0],
    products_services: products,
    bots: [
      {
        name: `${name} Partner Portal`,
        type: 'external',
        link: `https://assist.quad.example/${accountId}`,
        api_endpoint: `https://api.quad.example/chatbots/${accountId}/chat`,
        purpose: `Partner assistant for ${name} account management and materials.`,
        bound_spaces: [
          {
            id: `space-${accountId}-partner`,
            name: `${name} Partner View`,
            visibility: 'Partner',
          },
        ],
      },
      {
        name: 'QuadChat',
        type: 'internal',
        link: 'https://spaces.sprinklenet.com/chat?id=68f2b29111ce1212673e5d87',
        api_endpoint:
          'https://spaces.sprinklenet.com/api/chatbots/68f2b29111ce1212673e5d87/chat',
        api_snippet:
          'https://spaces.sprinklenet.com/api/chatbots/68f2b29111ce1212673e5d87/chat',
        purpose: `Internal support for ${name} account operations.`,
        bound_spaces: [
          {
            id: 'space-quad-internal',
            name: 'Quad Internal Knowledge Space',
            visibility: 'Internal',
          },
        ],
      },
    ],
    people: [...client_contacts, ...quad_team],
    client_contacts,
    quad_team,
    assets: [],
    kpis: {
      active_jobs: 3 + Math.floor(Math.random() * 15),
      open_quotes: Math.floor(Math.random() * 8),
      sla_alerts: health === 'Red' ? 1 + Math.floor(Math.random() * 3) : 0,
    },
    last_sync: lastSync.toISOString(),
  }
}
// Generate 100 accounts across diverse industries
export const generatedAccounts: Account[] = [
  // Retail (15 accounts)
  generateAccount(1, 'NorthStar Retail Group', 'Retail', [
    'Direct Mail',
    'In-Store Marketing',
    'Digital',
  ]),
  generateAccount(2, 'Everline Department Stores', 'Retail', [
    'Catalogs',
    'Direct Mail',
    'Packaging',
  ]),
  generateAccount(3, 'Summit Fashion Brands', 'Retail', [
    'Marketing Services',
    'Digital',
    'Packaging',
  ]),
  generateAccount(4, 'Cascade Home Goods', 'Retail', [
    'In-Store Marketing',
    'Catalogs',
    'Direct Mail',
  ]),
  generateAccount(5, 'Pinnacle Sporting Goods', 'Retail', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(6, 'Horizon Furniture Co', 'Retail', [
    'Catalogs',
    'In-Store Marketing',
    'Packaging',
  ]),
  generateAccount(7, 'Crestview Apparel', 'Retail', [
    'Direct Mail',
    'Marketing Services',
    'Digital Signage',
  ]),
  generateAccount(8, 'Meadowbrook Home Center', 'Retail', [
    'In-Store Marketing',
    'Direct Mail',
    'Catalogs',
  ]),
  generateAccount(9, 'Riverside Electronics', 'Retail', [
    'Marketing Services',
    'Digital',
    'Packaging',
  ]),
  generateAccount(10, 'Lakeside Garden Supply', 'Retail', [
    'Direct Mail',
    'Catalogs',
    'In-Store Marketing',
  ]),
  generateAccount(11, 'Parkway Auto Parts', 'Retail', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(12, 'Westgate Office Solutions', 'Retail', [
    'Catalogs',
    'Direct Mail',
    'Packaging',
  ]),
  generateAccount(13, 'Bridgewater Pet Supplies', 'Retail', [
    'In-Store Marketing',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(14, 'Clearview Pharmacy Chain', 'Retail', [
    'Direct Mail',
    'In-Store Marketing',
    'Marketing Services',
  ]),
  generateAccount(15, 'Oakmont Specialty Foods', 'Retail', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  // Finance (12 accounts)
  generateAccount(16, 'Union Bank Corporation', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(17, 'Meridian Credit Union', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Logistics',
  ]),
  generateAccount(18, 'Cornerstone Financial Group', 'Financial Services', [
    'Direct Mail',
    'Digital',
    'Marketing Services',
  ]),
  generateAccount(19, 'Horizon Investment Partners', 'Financial Services', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(20, 'Summit Capital Advisors', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Packaging',
  ]),
  generateAccount(21, 'Pinnacle Insurance Services', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(22, 'Northpoint Wealth Management', 'Financial Services', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(23, 'Gateway Credit Solutions', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Logistics',
  ]),
  generateAccount(24, 'Riverside Mortgage Company', 'Financial Services', [
    'Direct Mail',
    'Digital',
    'Marketing Services',
  ]),
  generateAccount(25, 'Crestline Banking Group', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(26, 'Brookfield Asset Management', 'Financial Services', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(27, 'Fairview Financial Services', 'Financial Services', [
    'Direct Mail',
    'Marketing Services',
    'Logistics',
  ]),
  // Healthcare (12 accounts)
  generateAccount(28, 'Vantage Health Systems', 'Healthcare', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(29, 'Harmony Medical Group', 'Healthcare', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(30, 'Sunrise Healthcare Network', 'Healthcare', [
    'Direct Mail',
    'Marketing Services',
    'In-Store Marketing',
  ]),
  generateAccount(31, 'Beacon Hospital Alliance', 'Healthcare', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(32, 'Crossroads Medical Centers', 'Healthcare', [
    'Direct Mail',
    'Marketing Services',
    'Logistics',
  ]),
  generateAccount(33, 'Summit Specialty Clinics', 'Healthcare', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(34, 'Evergreen Urgent Care', 'Healthcare', [
    'Direct Mail',
    'Marketing Services',
    'In-Store Marketing',
  ]),
  generateAccount(35, 'Parkview Dental Group', 'Healthcare', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(36, 'Northshore Vision Centers', 'Healthcare', [
    'Direct Mail',
    'Marketing Services',
    'In-Store Marketing',
  ]),
  generateAccount(37, 'Lakeside Physical Therapy', 'Healthcare', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(38, 'Ridgeline Orthopedics', 'Healthcare', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(39, 'Meadow Springs Wellness', 'Healthcare', [
    'Marketing Services',
    'Direct Mail',
    'In-Store Marketing',
  ]),
  // Media & Publishing (10 accounts)
  generateAccount(40, 'NextWave Media Group', 'Media & Publishing', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  generateAccount(41, 'Horizon Broadcasting', 'Media & Publishing', [
    'Marketing Services',
    'Digital Signage',
    'Direct Mail',
  ]),
  generateAccount(42, 'Skyline Publishing House', 'Media & Publishing', [
    'Catalogs',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(43, 'Crossroads News Network', 'Media & Publishing', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  generateAccount(44, 'Summit Magazine Group', 'Media & Publishing', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(45, 'Riverside Entertainment', 'Media & Publishing', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  generateAccount(46, 'Pinnacle Productions', 'Media & Publishing', [
    'Marketing Services',
    'Digital Signage',
    'Direct Mail',
  ]),
  generateAccount(47, 'Gateway Digital Media', 'Media & Publishing', [
    'Digital',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(48, 'Northpoint Press Group', 'Media & Publishing', [
    'Direct Mail',
    'Catalogs',
    'Marketing Services',
  ]),
  generateAccount(49, 'Clearview Communications', 'Media & Publishing', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  // Technology (10 accounts)
  generateAccount(50, 'Vertex Technologies', 'Technology', [
    'Marketing Services',
    'Digital',
    'Packaging',
  ]),
  generateAccount(51, 'Nexus Software Solutions', 'Technology', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  generateAccount(52, 'Quantum Computing Corp', 'Technology', [
    'Digital',
    'Marketing Services',
    'Packaging',
  ]),
  generateAccount(53, 'Cascade Cloud Services', 'Technology', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  generateAccount(54, 'Pinnacle Data Systems', 'Technology', [
    'Digital',
    'Marketing Services',
    'Packaging',
  ]),
  generateAccount(55, 'Horizon Tech Group', 'Technology', [
    'Marketing Services',
    'Digital',
    'Direct Mail',
  ]),
  generateAccount(56, 'Summit Cybersecurity', 'Technology', [
    'Marketing Services',
    'Digital',
    'Packaging',
  ]),
  generateAccount(57, 'Northstar AI Solutions', 'Technology', [
    'Digital',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(58, 'Gateway Networks Inc', 'Technology', [
    'Marketing Services',
    'Digital',
    'Packaging',
  ]),
  generateAccount(59, 'Riverside Tech Partners', 'Technology', [
    'Digital',
    'Marketing Services',
    'Direct Mail',
  ]),
  // Travel & Hospitality (8 accounts)
  generateAccount(60, 'Horizon Airlines', 'Travel & Hospitality', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(61, 'Summit Hotel Group', 'Travel & Hospitality', [
    'In-Store Marketing',
    'Direct Mail',
    'Digital Signage',
  ]),
  generateAccount(62, 'Parkway Travel Services', 'Travel & Hospitality', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(63, 'Cascade Resorts International', 'Travel & Hospitality', [
    'Direct Mail',
    'Marketing Services',
    'Digital Signage',
  ]),
  generateAccount(64, 'Northpoint Cruise Lines', 'Travel & Hospitality', [
    'Marketing Services',
    'Direct Mail',
    'Digital',
  ]),
  generateAccount(65, 'Gateway Vacation Rentals', 'Travel & Hospitality', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(66, 'Riverside Conference Centers', 'Travel & Hospitality', [
    'In-Store Marketing',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(67, 'Clearview Travel Agency', 'Travel & Hospitality', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  // Manufacturing (10 accounts)
  generateAccount(68, 'Precision Manufacturing Co', 'Manufacturing', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(69, 'Summit Industrial Group', 'Manufacturing', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(70, 'Northstar Components', 'Manufacturing', [
    'Packaging',
    'Marketing Services',
    'Logistics',
  ]),
  generateAccount(71, 'Cascade Materials Corp', 'Manufacturing', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(72, 'Pinnacle Engineering', 'Manufacturing', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(73, 'Horizon Automotive Parts', 'Manufacturing', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(74, 'Gateway Electronics Manufacturing', 'Manufacturing', [
    'Packaging',
    'Marketing Services',
    'Logistics',
  ]),
  generateAccount(75, 'Riverside Industrial Supply', 'Manufacturing', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(76, 'Crossroads Machinery', 'Manufacturing', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(77, 'Meadowbrook Tool & Die', 'Manufacturing', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  // Consumer Goods (10 accounts)
  generateAccount(78, 'Everline Consumer Products', 'Consumer Goods', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(79, 'Summit Beverage Company', 'Consumer Goods', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(80, 'Horizon Food Group', 'Consumer Goods', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(81, 'Northpoint Personal Care', 'Consumer Goods', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(82, 'Cascade Household Products', 'Consumer Goods', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(83, 'Pinnacle Snack Foods', 'Consumer Goods', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(84, 'Gateway Pet Products', 'Consumer Goods', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(85, 'Riverside Nutrition Co', 'Consumer Goods', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  generateAccount(86, 'Clearview Home Care', 'Consumer Goods', [
    'Packaging',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(87, 'Lakeside Organic Foods', 'Consumer Goods', [
    'Packaging',
    'Marketing Services',
    'Direct Mail',
  ]),
  // Logistics & Distribution (5 accounts)
  generateAccount(88, 'Everline Logistics', 'Logistics & Distribution', [
    'Logistics Services',
    'Marketing Services',
    'Packaging',
  ]),
  generateAccount(89, 'Summit Supply Chain', 'Logistics & Distribution', [
    'Logistics Services',
    'Direct Mail',
    'Marketing Services',
  ]),
  generateAccount(
    90,
    'Northstar Freight Services',
    'Logistics & Distribution',
    ['Logistics Services', 'Marketing Services', 'Packaging'],
  ),
  generateAccount(
    91,
    'Cascade Distribution Group',
    'Logistics & Distribution',
    ['Logistics Services', 'Direct Mail', 'Marketing Services'],
  ),
  generateAccount(92, 'Gateway Warehousing', 'Logistics & Distribution', [
    'Logistics Services',
    'Marketing Services',
    'Packaging',
  ]),
  // Government & Education (5 accounts)
  generateAccount(93, 'State Department of Commerce', 'Government', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(94, 'County Public Works', 'Government', [
    'Direct Mail',
    'Marketing Services',
    'In-Store Marketing',
  ]),
  generateAccount(95, 'Regional Transit Authority', 'Government', [
    'Direct Mail',
    'Marketing Services',
    'Digital Signage',
  ]),
  generateAccount(96, 'University System Foundation', 'Education', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(97, 'Community College District', 'Education', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  // Energy & Utilities (3 accounts)
  generateAccount(98, 'Horizon Energy Solutions', 'Energy & Utilities', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
  generateAccount(99, 'Summit Power & Light', 'Energy & Utilities', [
    'Direct Mail',
    'Marketing Services',
    'In-Store Marketing',
  ]),
  generateAccount(100, 'Northpoint Water District', 'Energy & Utilities', [
    'Direct Mail',
    'Marketing Services',
    'Digital',
  ]),
]
